;(function (angular, _, moment) {
  'use strict'

  class CreditNoteService {
    $q
    $http
    $state
    exportService
    preferencesService
    creditNoteManager
    snackService
    statusService
    utilService
    paymentService
    urls
    $uibModal

    constructor (
      // Angular
      $http, $q, $state,
      // Libs
      $uibModal,
      // Managers
      CreditNoteManager,
      // Services
      ExportService, PaymentService, PreferencesService, SnackService, StatusService, UtilService,
      // Const
      urls
      // Resolved
    ) {
      this.$http = $http
      this.$q = $q
      this.$state = $state
      this.$uibModal = $uibModal
      this.creditNoteManager = CreditNoteManager
      this.exportService = ExportService
      this.paymentService = PaymentService
      this.preferencesService = PreferencesService
      this.snackService = SnackService
      this.statusService = StatusService
      this.utilService = UtilService
      this.urls = urls
    }

    get manager () {
      return this.creditNoteManager
    }

    createCreditNoteFromPreferences () {
      return this.$q.all([
        this.utilService.getLegalNotices(),
        this.preferencesService.getCreditNotePrefs()
      ]).then(([legalNotices, creditNotePrefs]) => {
        return this.creditNoteManager.create({
          status: 'draft',
          history: [],
          conditions: _.get(creditNotePrefs, 'defaultConditions', '').replace(/\n/g, '<br>'),
          thanks: _.get(creditNotePrefs, 'defaultThanks', '').replace(/\n/g, '<br>'),
          legalNotices: legalNotices,
          customFields: {}
        })
      })
    }

    /**
     * Return dueDate for credit note based on preferences
     * @param creditNote {CreditNote}
     * @param date {Date}
     * @return {Promise<moment>}
     */
    getDueDate (creditNote, date = new Date()) {
      const _date = moment(date)
      return this.preferencesService.getPrefsFor('creditnote').then((prefs) => {
        if (creditNote?.client?.paymentDelay) {
          if (creditNote.client.paymentDelayEndOfMonth) {
            return moment(_date).add(creditNote.client.paymentDelay, 'days').endOf('month').toDate()
          }
          return moment(_date).add(creditNote.client.paymentDelay, 'days').toDate()
        }
        if (prefs.creditNoteDelay !== 0) {
          if (prefs.creditNoteDelayEndOfMonth) {
            return moment(_date).add(prefs.creditNoteDelay, 'days').endOf('month').toDate()
          }
          return moment(_date).add(prefs.creditNoteDelay, 'days').toDate()
        }
        return moment(_date).toDate()
      })
    }

    /**
     * Return available status for given credit note
     * @param creditNote {CreditNote}
     * @return {*}
     */
    getAllowedStatus (creditNote) {
      return this.statusService.getAllowedStatus('creditnote', creditNote.status).filter(v => v !== 'paid')
    }

    asPdf (creditNoteId) {
      return this.exportService.creditNoteAsPdf(creditNoteId)
    }

    asCsv (creditNoteId) {
      return this.exportService.creditNoteAsCsv(creditNoteId)
    }

    asSpreadsheet (creditNoteId) {
      return this.exportService.creditNoteAsSpreadsheet(creditNoteId)
    }

    /**
     * Update credit note status
     * @param creditNote {CreditNote}
     * @param status {'validated' | 'sent' | 'partly_paid'}
     * @return {Promise<CreditNote>}
     */
    setStatus (creditNote, status, cancelInvoice) {
      switch (status) {
        case 'validated':
          return this._validate(creditNote, cancelInvoice)
        case 'sent':
          return this._sendCreditNote(creditNote, cancelInvoice)
        case 'partly_paid':
          return this.makePayment(creditNote)
      }
    }

    changeDueDate (creditNote) {
      if (creditNote.immediatePayment) {
        creditNote.dueDate = null
        return this.$q.resolve(creditNote)
      }
      return this.getDueDate(creditNote, creditNote.date).then(v => {
        creditNote.dueDate = v
        return creditNote
      })
    }

    makePayment (creditNote) {
      return this.paymentService.addCreditNotePayment(creditNote)
    }

    duplicate (creditNote) {

      this.preferencesService.getCreditNotePrefs().then((prefs) => {
        const _creditNote = this.manager.create(creditNote.duplicate())
        if (!creditNote.dueDate && !creditNote.immediatePayment) {
          if (creditNote.client && creditNote.client.paymentDelay) {
            _creditNote.dueDate = moment(new Date()).add(creditNote.client.paymentDelay, 'days').toDate()
          } else {
            _creditNote.dueDate = moment(new Date()).add(prefs.creditNoteDelay, 'days').toDate()
          }
        }
        if (creditNote.chorusFields) delete creditNote.chorusFields
        if (creditNote.numberFormat) delete creditNote.numberFormat
        creditNote.origin = 'duplicate'
        this.$state.go('creditnote.new', { initParams: _creditNote })
        this.snackService.snack({
          text: 'Avoir dupliqué !',
          delay: 5000
        })
      })
    }

    // Privates

    _validate (creditNote, cancelInvoice) {
      if (cancelInvoice) {
        return this._switchToStatus(creditNote, 'validated', cancelInvoice)
      } else {
        return this.preferencesService.getCreditNotePrefs().then((creditNotePrefs) => {
          const emitDate = moment(creditNote.date).format('YYYY-MM-DD')
          const emitDateStart = creditNotePrefs?.emitPeriod?.start ? moment(creditNotePrefs.emitPeriod.start).format('YYYY-MM-DD') : null
          const emitDateEnd = creditNotePrefs?.emitPeriod?.end ? moment(creditNotePrefs.emitPeriod.end).format('YYYY-MM-DD') : null
          if (!(emitDateEnd && emitDateStart) || moment(emitDate).isBetween(emitDateStart, emitDateEnd, null, '[]')) {
            return this.$uibModal.open({
              controllerAs: 'valid',
              bindToController: true,
              resolve: {
                objType: function () { return 'creditnote' },
                date: _.get(creditNote, 'date', new Date())
              },
              templateUrl: '/app/modules/invoice/show/templates/validate.template.html',
              controller: 'ValidateInvoiceController'
            }).result.then((result) => {
              if (result) {
                return this._switchToStatus(creditNote, 'validated').then((r) => {
                  this.snackService.snack({
                    text: 'Avoir validé !',
                    delay: 5000
                  })
                  return r
                })
              }
            })
          }
          return this.$uibModal.open({
            controllerAs: 'invalid',
            bindToController: true,
            size: 'lg',
            resolve: {
              invalidType: [() => 'date']
            },
            templateUrl: '/app/modules/creditnote/show/templates/validate-error-template.html',
            controller: 'ValidateErrorController'
          }).result.then((result) => {
            return this.$q.reject()
          })
        })
      }
    }

    _sendCreditNote (creditNote, cancelInvoice) {
      if (cancelInvoice) {
        return this._switchToStatus(creditNote, 'sent')
      } else {
        return this.$uibModal.open({
          controllerAs: 'send',
          bindToController: true,
          resolve: {
            obj: function () {
              return creditNote
            },
            type: function () {
              return 'creditnote'
            },
            asInter: false
          },
          templateUrl: '/app/modules/invoice/show/templates/send-invoice.template.html',
          controller: 'SendInvoiceController'
        }).result.then((result) => {
          const promises = []
          if (result.mail) {
            const recipients = result.emails.filter(function (email) {
              return email.added
            }).map(function (e) { return { Email: e.email } })
            let url = this.urls.backend + '/services/pdf/creditnote/emails/'
            if (result.mailTemplate) {
              url += result.mailTemplate._id
            }
            const toSend = { creditNotes: [ creditNote._id ], recipients: recipients, email: result.customMailTemplate, subject: result.subject }
            if (result.model && result.model.length > 0) {
              toSend.model = result.model
              toSend.smtp = true
            }
            promises.push(this.$http.post(url, toSend))
          }
          if (result.print) {
            // TODO: print
          }
          if (result.status) {
            promises.push(this._switchToStatus(creditNote, 'sent'))
          }
          return this.$q.all(promises).then((results) => {
            this.snackService.snack({
              text: 'Avoir envoyé !',
              delay: 5000
            })
            if (!result.status) {
              return creditNote
            }
            // Get updated credit note from promises
            return results[results.length - 1]
          })
        })
      }
    }

    _switchToStatus (creditNote, status, cancelInvoice) {
      if (status === 'validated') {
        return this.preferencesService.getCreditNotePrefs().then((creditNotePrefs) => {
          creditNote.periodicity = creditNotePrefs.periodicity
          creditNote.resetDate = creditNotePrefs.resetDate
          creditNote.numberFormat = creditNotePrefs.numberFormat
          creditNote.status = status
          if (!creditNote.date) creditNote.date = new Date()
          if (!creditNote.dueDate && !creditNote.immediatePayment) {
            return this.changeDueDate(creditNote).then((r) => {
              return r.validate()
            })
          } else {
            return creditNote.validate()
          }
        })
      }
      return creditNote.setStatus(status)
    }

    asFacturX (invoiceId) {
      return this.exportService.creditNoteAsFacturX(invoiceId)
    }

    searchToSpreadsheet (conditions, total) {
      return this.exportService.searchToSpreadsheet(conditions, total, 'creditnote')
    }
  }

  CreditNoteService.$inject = [
    // Angular
    '$http', '$q', '$state',
    // Libs
    '$uibModal',
    // Managers
    'CreditNoteManager',
    // Services
    'ExportService', 'PaymentService', 'PreferencesService', 'SnackService', 'StatusService', 'UtilService',
    // Const
    'urls'
    // Resolved
  ]
  angular
    .module('kilo.common.services')
    .service('CreditNoteService', CreditNoteService)
})(window.angular, window._, window.moment)
